import { useMemo } from "react";
import { Link, Outlet, useNavigate, useRouteLoaderData, useSearchParams } from "react-router-dom";
import UserInfo from "src/components/common/userInfo";
import ServerFooter from "src/components/common/ServerFooter";
import {
    boolAndReducer,
    calcLocate,
    getDatefromWeekNum,
    getDateEndFromWeekNum,
    locatorExceptionsList,
    locatorWeeks,
    numberReducer,
    pmEmails,
    statusColorCode,
    weekDays,
} from "src/components/common/functions";
import DataReport from "./dataReport";
import Export from "./export";
import PMReport from "./pmReport";
import { LocatorListRow } from "./actions_loaders";

type LoaderData = {
    week: string;
    rows: LocatorListRow[];
};

export default function AllLocatorList() {
    const { week, rows } = useRouteLoaderData("AllLocatorList") as LoaderData;
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const locatorExceptionEmails = locatorExceptionsList as string[];
    const pmEmailList = pmEmails as string[];
    const query = (searchParams.get("q") ?? "").toLowerCase();
    const status = searchParams.get("status") ?? "";
    const showHours = searchParams.get("showHours") !== "0";
    const showMobHours = searchParams.get("showMobHours") === "1";
    const showTickets = searchParams.get("showTickets") === "1";

    const filteredRows = useMemo(() => rows
    .map(row => ({
        ...row,
        weekData: {
            ...row.weekData,
            form: (row.weekData.form ?? []).map((day, dayIndex) => calcLocate(day, dayIndex, getDatefromWeekNum(week, dayIndex) || "", row.outside)),
        },
    }))
    .filter(row => (
        !locatorExceptionEmails.includes(row.email) &&
        !pmEmailList.includes(row.email) &&
        (status === "Submitted" ? row.weekData.status === "Submitted" : true) &&
        (status === "Created" ? row.weekData.status !== "Submitted" : true) &&
        query.split(" ").filter(Boolean).map(word => JSON.stringify(row).toLowerCase().includes(word)).reduce(boolAndReducer, true)
    )), [rows, status, query, week, locatorExceptionEmails, pmEmailList]);

    const legacyFilteredForms = useMemo(() => filteredRows.map(row => ({
        id: row.id,
        email: row.email,
        userName: row.userName,
        lanId: row.lanId,
        outside: row.outside,
        hireDate: row.hireDate,
        quitDate: row.quitDate,
        outsideTimeCards: row.outsideTimeCards,
        [week]: row.weekData,
    })), [filteredRows, week]);

    const page = Number(searchParams.get("page") ?? 1);
    const limit = Number(searchParams.get("limit") ?? 10);
    const currentRows = filteredRows.slice((page - 1) * limit, page * limit);

    const setParam = (key: string, value: string) => {
        const params = new URLSearchParams(searchParams);
        if (value) params.set(key, value);
        else params.delete(key);
        if (key !== "page") params.set("page", "1");
        setSearchParams(params, { replace: false });
    };

    const renderSummary = (row: LocatorListRow) => {
        const form = row.weekData.form ?? [];
        const regular = form.map((day: any) => day.regular ?? 0).reduce(numberReducer, 0);
        const mobReg = form.map((day: any) => day.mobReg ?? 0).reduce(numberReducer, 0);
        const ot = form.map((day: any) => day.ot ?? 0).reduce(numberReducer, 0);
        const mobOT = form.map((day: any) => day.mobOT ?? 0).reduce(numberReducer, 0);
        const tickets = form.filter((day: any) => day.status === "Submitted").map((day: any) => day.data?.length ?? 0).reduce(numberReducer, 0);
        return (
            <>
                {showHours && regular ? <div>Regular: {regular.toFixed(2)}h</div> : null}
                {showMobHours && mobReg ? <div>Regular Mob: {mobReg.toFixed(2)}h</div> : null}
                {showHours && ot ? <div>OT: {ot.toFixed(2)}h</div> : null}
                {showMobHours && mobOT ? <div>OT Mob: {mobOT.toFixed(2)}h</div> : null}
                {showTickets && tickets ? <div># of tickets: {tickets}</div> : null}
            </>
        );
    };

    return (
        <div className = "cssContent pt-0 pb8rem">
            <nav className = "my-3 d-flex flex-wrap align-items-center gap-3">
                <span className = "d-flex align-items-center">
                    <label htmlFor = "locator-week" className = "me-2 mb-0">Week</label>
                    <select
                        id = "locator-week"
                            className = "form-control inline"
                            value = {week}
                            onChange = {e => {
                                navigate({
                                    pathname: `/OutsideStatus/${e.currentTarget.value}`,
                                    search: searchParams.toString(),
                                });
                            }}
                        >
                        {locatorWeeks.map((value, index) => <option key = {index} value = {value}>{getDateEndFromWeekNum(value)}</option>)}
                    </select>
                </span>
                <span className = "d-flex align-items-center">
                    <label htmlFor = "locator-status" className = "me-2 mb-0">Status</label>
                    <select
                        id = "locator-status"
                        className = "form-control inline"
                        value = {status}
                        onChange = {e => setParam("status", e.currentTarget.value)}
                    >
                        <option value = "">All</option>
                        <option>Submitted</option>
                        <option value = "Created">Unsubmitted</option>
                    </select>
                </span>
                <input
                    className = "form-control inline"
                    style = {{ maxWidth: 240 }}
                    type = "text"
                    placeholder = "Search"
                    value = {query}
                    onChange = {e => setParam("q", e.currentTarget.value)}
                />
                <label className = "d-flex align-items-center gap-2 mb-0">
                    <input type = "checkbox" checked = {showHours} onChange = {e => setParam("showHours", e.currentTarget.checked ? "1" : "0")} />
                    Hours
                </label>
                <label className = "d-flex align-items-center gap-2 mb-0">
                    <input type = "checkbox" checked = {showMobHours} onChange = {e => setParam("showMobHours", e.currentTarget.checked ? "1" : "0")} />
                    Mob
                </label>
                <label className = "d-flex align-items-center gap-2 mb-0">
                    <input type = "checkbox" checked = {showTickets} onChange = {e => setParam("showTickets", e.currentTarget.checked ? "1" : "0")} />
                    Tickets
                </label>
                {legacyFilteredForms.length ? (
                    <>
                        <PMReport filteredForms = {legacyFilteredForms} week = {week} />
                        <DataReport filteredForms = {legacyFilteredForms} week = {week} />
                        <Export />
                    </>
                ) : null}
            </nav>

            {!currentRows.length ? <div className = "cssLoading">Empty</div> : (
                <div className = "table-responsive">
                    <table className = "table text-center tableAllBorder">
                        <thead>
                            <tr className = "stickyHeader">
                                <th>Name</th>
                                {weekDays.map((day, dayIndex) => (
                                    <th key = {dayIndex}>
                                        {day} ({getDatefromWeekNum(week, dayIndex)})
                                    </th>
                                ))}
                                {(showHours || showMobHours || showTickets) ? <th>Summary</th> : null}
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {currentRows.map((row) => (
                                <tr key = {row.id || row.email}>
                                    <th>
                                        <div>
                                            <UserInfo name = {row.userName.replace(/ {2}/g, " ")} id = {row.id || row.email} />
                                            <span className = "badge badge-primary mx-2">{row.weekData.status || "Created"}</span>
                                        </div>
                                        <div>{row.hireDate}</div>
                                        {row.quitDate ? <div className = "text-danger">{row.quitDate}</div> : null}
                                    </th>
                                    {(row.weekData.form ?? []).map((day: any, index: number) => (
                                        <th key = {index}>
                                            <div
                                                style = {{ color: statusColorCode(day.status || "Created", "Work") }}
                                                className = "fw-bold"
                                            >
                                                {day.workStatus || "Work"} {day.status || "Created"}
                                            </div>
                                            {showHours && day.regular ? <div>Regular: {day.regular.toFixed(2)}h</div> : null}
                                            {showMobHours && day.mobReg ? <div>Regular Mob: {day.mobReg.toFixed(2)}h</div> : null}
                                            {showHours && day.ot ? <div>OT: {day.ot.toFixed(2)}h</div> : null}
                                            {showMobHours && day.mobOT ? <div>OT Mob: {day.mobOT.toFixed(2)}h</div> : null}
                                            {showTickets && day.status === "Submitted" && day.data?.length ? <div># of tickets: {day.data.length}</div> : null}
                                        </th>
                                    ))}
                                    {(showHours || showMobHours || showTickets) ? <th>{renderSummary(row)}</th> : null}
                                    <td>
                                        <Link className = "btn btn-outline-primary" to = {String(row.id)}>Detail</Link>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {filteredRows.length ? <ServerFooter length = {filteredRows.length} /> : null}
            <Outlet />
        </div>
    );
}
